import {IRestService} from "./IRestService";
import {IPartService} from "./IPartService";
import LzString from "lz-string";
import { AuthenticationService } from "./AuthenticationService";
import { normalizePartWritePayload, normalizePartWritePayloadArray } from "../helpers/apiEntityPayloads";

export class PartService implements IPartService {
    
    public constructor(private readonly restService: IRestService) {
    }

    public async getAllVendors(): Promise<any[]> {
        return this.restService.get(`parts/vendors`);
    }

    public async createVendor(vendor: any): Promise<any> {
        return this.restService.post(`parts/vendor`, vendor);
    }

    public getById(id: string): Promise<any> {
        return this.restService.get(`parts/${id}`);
    }

    public getByReferenceId(id: string): Promise<any> {
        return this.restService.get(`parts/reference/${id}`);
    }

    public getAllParts(): Promise<any[]> {
        return this.restService.get(`parts`);
    }

    public async getAllPartsExpanded(): Promise<any[]> {
        const storageUpdated = localStorage.getItem('partsUpdated');
        const cachedCompressed = localStorage.getItem('parts');
        if (!storageUpdated || storageUpdated === '' || !cachedCompressed) {
            return this.restService.get(`parts/expanded`).then(async (assemblies: any[]) => {
                localStorage.setItem('parts', LzString.compress(JSON.stringify(assemblies)));
                const updatedOn = await this.getTableUpdatedDate('Parts');
                localStorage.setItem('partsUpdated', updatedOn);
                return assemblies;
            });
        }
        return this.getTableUpdatedDate('Parts').then((updatedOn) => {
            if (Date.parse(updatedOn) > Date.parse(storageUpdated)) {
                return this.restService.get(`parts/expanded`).then((assemblies: any[]) => {
                    localStorage.setItem('parts', LzString.compress(JSON.stringify(assemblies)));
                    localStorage.setItem('partsUpdated', updatedOn);
                    return assemblies;
                });
            } else {
                return JSON.parse(LzString.decompress(localStorage.getItem('parts')));
            }
        });
    }

    public async createPart(part: any): Promise<any> {
        const result = await this.restService.post(`parts`, normalizePartWritePayload(part));
        
        return result;
    }

    public async updatePart(part: any): Promise<any> {
        const result = await this.restService.put(`parts`, normalizePartWritePayload(part));
        
        return result;
    }

    public async updatePartNoCache(part: any): Promise<any> {
        const result = await this.restService.put(`parts`, normalizePartWritePayload(part));
        
        return result;
    }

    public async uploadPartImage(partId: string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        
        return this.restService.post(`parts/${partId}/upload-image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public async uploadPartFile(partId: string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        
        return this.restService.post(`parts/${partId}/upload-file`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public async uploadPartFamilyImage(partFamilyId: string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        
        return this.restService.post(`parts/part-family/${partFamilyId}/upload-image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public async deletePart(partId: string): Promise<any> {
        const result = await this.restService.delete(`parts/${partId}`);
        
        return result;
    }

    public async connectPartToAssembly(partId: string, assemblyId: string, quantity: string, bomRequired: boolean, partFamilyId?: string, order?: number, isImpliedPart?: boolean): Promise<any> {
        let url = `parts/${partId}/assembly/${assemblyId}/${quantity}/${bomRequired.toString()}`;
        const params = new URLSearchParams();
        if (partFamilyId) {
            params.append('partFamilyId', partFamilyId);
        }
        if (order !== undefined) {
            params.append('order', order.toString());
        }
        if (isImpliedPart !== undefined) {
            params.append('isImpliedPart', isImpliedPart.toString());
        }
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        return this.restService.post(url);
    }

    public async connectPartToSubAssembly(partId: string, subAssemblyId: string, quantity: string, bomRequired: boolean, partFamilyId?: string, order?: number, isImpliedPart?: boolean): Promise<any> {
        let url = `parts/${partId}/subAssembly/${subAssemblyId}/${quantity}/${bomRequired.toString()}`;
        const params = new URLSearchParams();
        if (partFamilyId) {
            params.append('partFamilyId', partFamilyId);
        }
        if (order !== undefined) {
            params.append('order', order.toString());
        }
        if (isImpliedPart !== undefined) {
            params.append('isImpliedPart', isImpliedPart.toString());
        }
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        return this.restService.post(url);
    }

    public async severPartToAssembly(partId: string, assemblyId: string): Promise<any> {
        return this.restService.delete(`parts/${partId}/assembly/${assemblyId}`);
    }

    public async severPartToSubAssembly(partId: string, subAssemblyId: string): Promise<any> {
        return this.restService.delete(`parts/${partId}/subAssembly/${subAssemblyId}`);
    }

    public async deletePartsToAssemblyConnection(connectionId: string): Promise<any> {
        return this.restService.delete(`parts/parts-to-assembly/${connectionId}`);
    }

    public async deletePartsToSubAssemblyConnection(connectionId: string): Promise<any> {
        return this.restService.delete(`parts/parts-to-subassembly/${connectionId}`);
    }

    public async updatePartsToAssembly(partsToAssembly: any): Promise<any> {
        return this.restService.put(`parts/parts-to-assembly`, partsToAssembly);
    }

    public async updatePartsToSubAssembly(partsToSubAssembly: any): Promise<any> {
        return this.restService.put(`parts/parts-to-subassembly`, partsToSubAssembly);
    }

    public async getInventoryLogs(): Promise<any[]> {
        return this.restService.get(`parts/inventory-logs`);
    }

    public async createInventoryLog(inventoryLog: any): Promise<any> {
        inventoryLog.userId = AuthenticationService.getInstance().getUserId();
        const result = this.restService.post(`parts/inventory-log`, inventoryLog);

        return result;
    }

    public async updateInventoryLog(inventoryLog: any): Promise<any> {
        return this.restService.put(`parts/inventory-log/${inventoryLog.id}`, inventoryLog);
    }

    public async deleteInventoryLog(inventoryLogId: string): Promise<any> {
        return this.restService.delete(`parts/inventory-log/${inventoryLogId}`);
    }

    public async createPurchaseLog(purchaseLog: any): Promise<any> {
        purchaseLog.userId = AuthenticationService.getInstance().getUserId();
        return this.restService.post(`parts/purchase-log`, purchaseLog);
    }

    public async getPurchaseLogs(): Promise<any[]> {
        return this.restService.get(`parts/purchase-logs`);
    }

    public async updatePurchaseLog(purchaseLog: any): Promise<any> {
        return this.restService.put(`parts/purchase-log/${purchaseLog.id}`, purchaseLog);
    }

    public async deletePurchaseLog(purchaseLogId: string): Promise<any> {
        return this.restService.delete(`parts/purchase-log/${purchaseLogId}`);
    }

    public async updatePurchaseOrder(purchaseOrder: any): Promise<any> {
        return this.restService.put(`parts/purchase-order/${purchaseOrder.id}`, purchaseOrder);
    }

    public async deletePurchaseOrder(purchaseOrderId: string): Promise<any> {
        return this.restService.delete(`parts/purchase-order/${purchaseOrderId}`);
    }

    public async importParts(parts: any[]): Promise<any[]> {
        return this.restService.post(`parts/import`, normalizePartWritePayloadArray(parts));
    }

    public importInventoryLogs(importedLogs: any[]): Promise<any[]> {
        return this.restService.post(`parts/import/inventory`, importedLogs);
    }

    public importPurchaseLogs(importedLogs: any[]): Promise<any[]> {
        return this.restService.post(`parts/import/purchase`, importedLogs);
    }

    public updatePartType(newValue: string, oldValue: string): Promise<any> {
        return this.restService.put(`parts/part-type`, {newValue: newValue, oldValue: oldValue});
    }

    public updateManufacturerFor(newValue: string, oldValue: string): Promise<any> {
        return this.restService.put(`parts/manufacturer-for`, {newValue: newValue, oldValue: oldValue});
    }

    public updateTertiaryCategory(newValue: string, oldValue: string): Promise<any> {
        return this.restService.put(`parts/tertiary-type`, {newValue: newValue, oldValue: oldValue});
    }

    public updateBrand(newValue: string, oldValue: string): Promise<any> {
        return this.restService.put(`parts/brand`, {newValue: newValue, oldValue: oldValue});
    }

    public getPartFamilies(): Promise<any[]> {
        return this.restService.get(`parts/part-families`);
    }

    public createPartFamily(partFamily: any): Promise<any> {
        return this.restService.post(`parts/part-family`, partFamily);
    }

    public updatePartFamily(partFamily: any): Promise<any> {
        return this.restService.put(`parts/part-family`, partFamily);
    }

    public deletePartFamily(partFamilyId: string): Promise<any> {
        return this.restService.delete(`parts/part-family/${partFamilyId}`);
    }

    public addPartToFamily(relationship: any): Promise<any> {
        return this.restService.post(`parts/families/parts`, relationship);
    }

    public removePartFromFamily(partId: string, familyId: string): Promise<any> {
        return this.restService.delete(`parts/families/${familyId}/parts/${partId}`);
    }

    public getPartFamilyRelationships(familyId: string): Promise<any[]> {
        return this.restService.get(`parts/families/${familyId}/parts`);
    }

    public updatePartFamilyRelationship(relationship: any): Promise<any> {
        return this.restService.put(`parts/families/parts`, relationship);
    }

    public updatePartFamilyOrder(familyId: string, partIds: string[]): Promise<any> {
        return this.restService.put(`parts/families/${familyId}/parts/order`, partIds);
    }

    public async getPurchaseOrders(): Promise<any[]> {
        return this.restService.get(`parts/purchase-orders`);
    }

    public createPurchaseOrder(purchaseOrder: any): Promise<any> {
        return this.restService.post(`parts/purchase-order`, purchaseOrder);
    }



    // New invoice methods
    public async getAllInvoices(): Promise<any[]> {
        return this.restService.get(`invoices`);
    }

    public async getInvoiceById(invoiceId: string): Promise<any> {
        return this.restService.get(`invoices/${invoiceId}`);
    }

    public async createInvoice(invoice: any, file?: File): Promise<any> {
        const formData = new FormData();
        formData.append('invoice', JSON.stringify(invoice));
        
        if (file) {
            formData.append('file', file);
        }

        return this.restService.post(`invoices`, formData);
    }

    public async updateInvoice(invoice: any): Promise<any> {
        return this.restService.put(`invoices/${invoice.id}`, invoice);
    }

    public async deleteInvoice(invoiceId: string): Promise<any> {
        return this.restService.delete(`invoices/${invoiceId}`);
    }



    public async updateInvoicePaidStatus(invoiceId: string, isPaid: boolean): Promise<any> {
        // Send the boolean value as a query parameter
        return this.restService.put(`invoices/${invoiceId}/paid-status?isPaid=${isPaid}`);
    }



    public createPurchaseOrderPackingSlip(formData: FormData): Promise<any> {
        return this.restService.post(`parts/packing-slip`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public receivePurchaseOrderPackingSlip(id: string, userId?: string): Promise<any> {
        const actualUserId = userId || AuthenticationService.getInstance().getUserId();
        return this.restService.post(`parts/packing-slip/${id}/receive`, { userId: actualUserId });
    }

    public getVendorNames(): Promise<string[]> {
        return this.restService.get(`parts/vendor-names`);
    }

    public getOutstandingPurchasedPartsByVendor(vendorName: string): Promise<any[]> {
        return this.restService.get(`parts/outstanding-parts/${encodeURIComponent(vendorName)}`);
    }

    public getAllPurchasedPartsByVendor(vendorName: string): Promise<any[]> {
        return this.restService.get(`parts/all-parts/${encodeURIComponent(vendorName)}`);
    }

    public receivePackingSlipWithQuantities(id: string, receivedQuantities: { [key: string]: number }, userId?: string): Promise<any> {
        const actualUserId = userId || AuthenticationService.getInstance().getUserId();
        return this.restService.post(`parts/packing-slip/${id}/receive-with-quantities`, { 
            receivedQuantities, 
            userId: actualUserId 
        });
    }

    public getAllPackingSlips(): Promise<any[]> {
        return this.restService.get(`parts/packing-slips`);
    }

    public deletePackingSlip(packingSlipId: string): Promise<any> {
        return this.restService.delete(`parts/packing-slip/${packingSlipId}`);
    }

    public getPackingSlipsByPurchaseOrderId(purchaseOrderId: string): Promise<any[]> {
        return this.restService.get(`parts/purchase-order/${purchaseOrderId}/packing-slips`);
    }

    public getPurchaseOrderPackingSlipById(packingSlipId: string): Promise<any> {
        return this.restService.get(`parts/packing-slip/${packingSlipId}`);
    }

    // Receiving Actions methods
    public getReceivingActionsByPackingSlipId(packingSlipId: string): Promise<any[]> {
        return this.restService.get(`parts/packing-slip/${packingSlipId}/receiving-actions`);
    }

    public getReceivingActionsByPurchasedPartId(purchasedPartId: string): Promise<any[]> {
        return this.restService.get(`parts/purchased-part/${purchasedPartId}/receiving-actions`);
    }

    public getReceivingActionsByPurchaseOrderId(purchaseOrderId: string): Promise<any[]> {
        return this.restService.get(`parts/purchase-order/${purchaseOrderId}/receiving-actions`);
    }

    public directReceive(request: { lines: Array<{ purchasedPartId: string; quantity: number; receivedDate?: string }>; userId: string }): Promise<any> {
        return this.restService.post(`parts/direct-receive`, request);
    }

    public createReceivingAction(packingSlipId: string, receivingAction: any): Promise<any> {
        return this.restService.post(`parts/packing-slip/${packingSlipId}/receiving-action`, receivingAction);
    }

    public updateReceivingAction(receivingActionId: string, receivingAction: any): Promise<any> {
        return this.restService.put(`parts/receiving-action/${receivingActionId}`, receivingAction);
    }

    public deleteReceivingAction(receivingActionId: string): Promise<any> {
        return this.restService.delete(`parts/receiving-action/${receivingActionId}`);
    }

    // Methods for managing purchased part invoice attachments
    public async attachInvoiceToPurchasedPart(purchasedPartId: string, invoiceId: string, quantity: number): Promise<any> {
        return this.restService.post(`parts/purchased-part/${purchasedPartId}/attach-invoice/${invoiceId}`, quantity);
    }

    public async bulkAttachInvoiceToPurchasedParts(invoiceId: string, purchasedPartQuantities: { [key: string]: number }): Promise<any> {
        return this.restService.post(`parts/invoice/${invoiceId}/attach-line-items`, purchasedPartQuantities);
    }

    public async detachInvoiceFromPurchasedPart(purchasedPartId: string, invoiceId: string): Promise<any> {
        return this.restService.delete(`parts/purchased-part/${purchasedPartId}/detach-invoice/${invoiceId}`);
    }

    public async getInvoicesForPurchasedPart(purchasedPartId: string): Promise<any[]> {
        return this.restService.get(`parts/purchased-part/${purchasedPartId}/invoices`);
    }

    public async getAllPurchasedPartInvoices(): Promise<{ [key: string]: any[] }> {
        return this.restService.get(`parts/purchased-parts/invoices/bulk`);
    }

    private getTableUpdatedDate(tableName: string): Promise<string> {
        return this.restService.get(`auth/${tableName}/check-updated`);
    }

    // Packing slip item management methods
    public updatePackingSlipItem(item: any): Promise<any> {
        return this.restService.put(`parts/packing-slip-item/${item.id}`, item);
    }

    public deletePackingSlipItem(itemId: string): Promise<any> {
        return this.restService.delete(`parts/packing-slip-item/${itemId}`);
    }

    public addItemToPackingSlip(item: any): Promise<any> {
        return this.restService.post(`parts/packing-slip-item`, item);
    }

    public updatePackingSlip(packingSlip: any): Promise<any> {
        return this.restService.put(`parts/packing-slip`, packingSlip);
    }

    public uploadPackingSlipFile(packingSlipId: string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);

        // Let the browser set multipart Content-Type with boundary
        return this.restService.post(`parts/packing-slip/${packingSlipId}/upload-file`, formData);
    }

    public clonePackingSlipFile(packingSlipId: string, sourceFileUrl: string, sourceFileName: string): Promise<any> {
        return this.restService.post(`parts/packing-slip/${packingSlipId}/clone-file`, {
            sourceFileUrl,
            sourceFileName
        });
    }

    public clonePackingSlipFileFromOtherSlip(packingSlipId: string, sourcePackingSlipId: string): Promise<any> {
        return this.restService.post(`parts/packing-slip/${packingSlipId}/clone-from-slip`, {
            sourcePackingSlipId
        });
    }
}
